import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, inject, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { FileVaultItem } from '../../models/file-vault.models';

export type ViewerMode = 'inline' | 'dialog' | 'fullscreen';

@Component({
  selector: 'fv-file-viewer',
  standalone: true,
  template: `
    <div class="file-viewer" [class.fullscreen]="mode === 'fullscreen'">
      <div class="viewer-toolbar" *ngIf="showToolbar">
        <div class="toolbar-left">
          <span class="file-name">{{ file?.fileName }}</span>
          <span class="file-type">{{ file?.fileExtension?.toUpperCase() }}</span>
        </div>
        <div class="toolbar-right">
          <button kendoButton (click)="download()" title="Download">Download</button>
          <button kendoButton (click)="zoomIn()" *ngIf="isImage" title="Zoom In">+</button>
          <button kendoButton (click)="zoomOut()" *ngIf="isImage" title="Zoom Out">-</button>
          <button kendoButton (click)="resetZoom()" *ngIf="isImage" title="Reset">Reset</button>
          <button kendoButton (click)="toggleFullscreen()" title="Fullscreen"></button>
          <button kendoButton (click)="close()" *ngIf="mode === 'dialog'" title="Close"></button>
        </div>
      </div>

      <div class="viewer-content" *ngIf="!isLoading && !error">
        <div class="image-viewer" *ngIf="isImage" [style.transform]="'scale(' + zoomLevel + ')'">
          <img #imageEl [src]="contentUrl" [alt]="file?.fileName || 'Image'" />
        </div>
        <div class="pdf-viewer" *ngIf="isPdf">
          <iframe [src]="safeUrl" type="application/pdf"></iframe>
        </div>
        <div class="text-viewer" *ngIf="isText">
          <pre>{{ textContent }}</pre>
        </div>
        <div class="video-viewer" *ngIf="isVideo">
          <video controls [src]="contentUrl">Your browser does not support video.</video>
        </div>
        <div class="unsupported" *ngIf="!isImage && !isPdf && !isText && !isVideo && contentUrl">
          <div class="unsupported-icon"></div>
          <p>Preview not available for this file type</p>
          <button kendoButton (click)="download()">Download File</button>
        </div>
      </div>

      <div class="viewer-loading" *ngIf="isLoading">
        <kendo-loader type="infinite-spinner" size="large"></kendo-loader>
        <p>Loading file...</p>
      </div>

      <div class="viewer-error" *ngIf="error">
        <div class="error-icon"></div>
        <p>{{ error }}</p>
        <button kendoButton (click)="retry()">Retry</button>
      </div>
    </div>
  `,
  styles: [`
    .file-viewer { height: 100%; display: flex; flex-direction: column; background: #1e1e1e; color: #fff; }
    .file-viewer.fullscreen { position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 10000; }
    .viewer-toolbar { display: flex; justify-content: space-between; align-items: center; padding: 8px 16px; background: #2d2d2d; border-bottom: 1px solid #444; }
    .toolbar-left { display: flex; align-items: center; gap: 12px; }
    .file-name { font-weight: 500; max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .file-type { padding: 2px 8px; background: #444; border-radius: 4px; font-size: 11px; }
    .toolbar-right { display: flex; gap: 8px; }
    .viewer-content { flex: 1; overflow: auto; display: flex; align-items: center; justify-content: center; }
    .image-viewer { transition: transform 0.2s ease; max-width: 100%; max-height: 100%; }
    .image-viewer img { max-width: 100%; max-height: 100%; object-fit: contain; }
    .pdf-viewer { width: 100%; height: 100%; }
    .pdf-viewer iframe { width: 100%; height: 100%; border: none; }
    .text-viewer { width: 100%; height: 100%; overflow: auto; padding: 16px; background: #1e1e1e; }
    .text-viewer pre { margin: 0; font-family: 'Consolas', monospace; font-size: 14px; line-height: 1.5; white-space: pre-wrap; word-wrap: break-word; }
    .video-viewer { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
    .video-viewer video { max-width: 100%; max-height: 100%; }
    .unsupported, .viewer-loading, .viewer-error { text-align: center; padding: 40px; }
    .unsupported-icon, .error-icon { font-size: 64px; margin-bottom: 16px; }
  `],
  imports: [CommonModule, ButtonsModule, IndicatorsModule]
})
export class FileViewerComponent implements OnChanges {
  private api = inject(FileVaultApiService);
  private sanitizer = inject(DomSanitizer);

  @ViewChild('imageEl') imageEl?: ElementRef<HTMLImageElement>;

  @Input() file?: FileVaultItem;
  @Input() mode: ViewerMode = 'inline';
  @Input() showToolbar = true;

  @Output() closed = new EventEmitter<void>();
  @Output() downloaded = new EventEmitter<FileVaultItem>();

  contentUrl?: string;
  safeUrl?: SafeResourceUrl;
  textContent?: string;
  isLoading = false;
  error?: string;
  zoomLevel = 1;

  private readonly imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  private readonly pdfExtensions = ['pdf'];
  private readonly textExtensions = ['txt', 'log', 'json', 'xml', 'csv', 'md', 'html', 'css', 'js', 'ts'];
  private readonly videoExtensions = ['mp4', 'webm', 'ogg', 'mov'];

  get isImage(): boolean { return this.imageExtensions.includes(this.file?.fileExtension?.toLowerCase() || ''); }
  get isPdf(): boolean { return this.pdfExtensions.includes(this.file?.fileExtension?.toLowerCase() || ''); }
  get isText(): boolean { return this.textExtensions.includes(this.file?.fileExtension?.toLowerCase() || ''); }
  get isVideo(): boolean { return this.videoExtensions.includes(this.file?.fileExtension?.toLowerCase() || ''); }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['file'] && this.file) { this.loadContent(); }
  }

  async loadContent(): Promise<void> {
    if (!this.file) return;
    this.isLoading = true; this.error = undefined; this.contentUrl = undefined; this.textContent = undefined; this.zoomLevel = 1;

    try {
      const blob = await this.api.downloadFile(this.file.id).toPromise();
      if (blob) {
        this.contentUrl = URL.createObjectURL(blob);
        if (this.isPdf) { this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.contentUrl); }
        if (this.isText) { this.textContent = await blob.text(); }
      }
    } catch (err) {
      this.error = 'Failed to load file content';
      console.error('Error loading file:', err);
    } finally {
      this.isLoading = false;
    }
  }

  retry(): void { this.loadContent(); }

  download(): void {
    const file = this.file;
    if (file) {
      this.api.downloadFile(file.id).subscribe(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.getDownloadFileName(file);
        a.click();
        URL.revokeObjectURL(url);
        this.downloaded.emit(file);
      });
    }
  }

  private getDownloadFileName(file: FileVaultItem): string {
    const name = file.fileName?.trim();
    const extension = file.fileExtension?.trim();
    if (!extension) {
      return name || 'file';
    }
    const normalizedExtension = extension.startsWith('.') ? extension : `.${extension}`;
    if (name?.endsWith(normalizedExtension)) {
      return name;
    }
    return `${name || 'file'}${normalizedExtension}`;
  }

  zoomIn(): void { this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3); }
  zoomOut(): void { this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.25); }
  resetZoom(): void { this.zoomLevel = 1; }
  toggleFullscreen(): void { this.mode = this.mode === 'fullscreen' ? 'inline' : 'fullscreen'; }

  close(): void {
    if (this.contentUrl) { URL.revokeObjectURL(this.contentUrl); }
    this.closed.emit();
  }
}
